/*
 * Copyright (c) 2024.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Pavel Bobylev <pavel.bobylev1988@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {
  Alphabet,
  CharacterCase,
  CharactersValidatorContent,
  IdKeyAndHeader,
  StringCustomValidatorContent,
  StringValidatorContent,
  StringValidatorType,
  translations
} from 'compob2b-libs';
import {CustomValidators} from './CustomValidators';

export class StringValidator {

  static symbols: IdKeyAndHeader[] = [
    {id: ' ', header: translations.spaceSymbol} as IdKeyAndHeader,
    {id: '_', header: translations.underscoreSymbol} as IdKeyAndHeader,
    {id: '.', header: translations.pointSymbol} as IdKeyAndHeader,
    {id: ',', header: translations.commaSymbol} as IdKeyAndHeader,
  ];

  static alphabets: IdKeyAndHeader[] = [
    {id: Alphabet.CYRILLIC, header: translations.cyrillicAlphabet} as IdKeyAndHeader,
    {id: Alphabet.LATIN, header: translations.latinAlphabet} as IdKeyAndHeader,
  ];

  static characterCases: IdKeyAndHeader[] = [
    {id: CharacterCase.UPPERCASE, header: translations.characterUpperCase} as IdKeyAndHeader,
    {id: CharacterCase.LOWERCASE, header: translations.characterLowerCase} as IdKeyAndHeader,
  ];

  public static getValidator(validator: StringValidatorContent): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string | null = control.value;
      if (value === null || value.trim() === '') {
        return null;
      }

      if (validator.typeValidator === StringValidatorType.EMAIL) {
        const emailValidatorResult = Validators.email(control);
        if (emailValidatorResult !== null) {
          return emailValidatorResult;
        }
      } else if (validator.typeValidator === StringValidatorType.URL) {
        const urlValidatorResult = CustomValidators.urlValidator(control);
        if (urlValidatorResult !== null) {
          return urlValidatorResult;
        }
      } else if (validator.typeValidator === StringValidatorType.CUSTOM) {
        const customStringValidatorResult = StringValidator.getCustomValidator(validator.customValidator)(control);
        if (customStringValidatorResult !== null) {
          return customStringValidatorResult;
        }
      }

      return null;
    };
  }

  public static getCustomValidator(customValidator: StringCustomValidatorContent): ValidatorFn {
    if (customValidator.useRegexp === true) {
      return Validators.pattern(customValidator.regexp);
    }

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value.trim().length === 0) {
        return null;
      }

      const validatorErrors: ValidationErrors = {};
      let valid = true;
      let stringContentValid = true;
      const stringContent: any = {};
      let fullStringPattern = '';
      let caseInsensitiveSearch = false;

      if (customValidator.minLength !== null && value.length < customValidator.minLength) {
        valid = false;
        validatorErrors.minlength = {requiredLength: customValidator.minLength, actualLength: value.length};
      }

      if (customValidator.maxLength !== null && value.length > customValidator.maxLength) {
        valid = false;
        validatorErrors.maxlength = {requiredLength: customValidator.maxLength, actualLength: value.length};
      }

      if (customValidator.useDigits === true) {
        fullStringPattern += '0-9';
        if (!value.match(/\d/)) {
          stringContentValid = false;
          stringContent.hasNotNumber = true;
        }
      }

      if (customValidator.useSymbols === true && Array.isArray(customValidator.symbols) && customValidator.symbols.length > 0) {
        stringContent.allowedSymbols = this.symbols
          .filter(symbol => customValidator.symbols.includes(symbol.id))
          .map(symbol => symbol.header);
        const symbolsPattern = customValidator.symbols.join('');
        fullStringPattern += symbolsPattern;
        if (!value.match(new RegExp('[' + symbolsPattern + ']'))) {
          stringContentValid = false;
          stringContent.hasNotSymbols = true;
        }
      }

      if (customValidator.useCharacters === true && Array.isArray(customValidator.characters.alphabets)
        && Array.isArray(customValidator.characters.charactersCases)) {
        stringContent.allowedCharactersCases = this.characterCases
          .filter(charactersCase => customValidator.characters.charactersCases.some(item => item === charactersCase.id))
          .map(charactersCase => charactersCase.header);
        stringContent.allowedAlphabets = this.alphabets
          .filter(alphabet => customValidator.characters.alphabets.some(item => item === alphabet.id))
          .map(alphabet => alphabet.header);

        const [charactersRegexp, useBothCases] = StringValidator.formCharactersPattern(customValidator.characters);
        caseInsensitiveSearch = useBothCases;
        fullStringPattern += charactersRegexp;
        if (!value.match(StringValidator.formCharactersRegexp(charactersRegexp, useBothCases))) {
          stringContentValid = false;
          stringContent.hasNotCharacters = true;
        }
      }

      if (fullStringPattern !== '' && !value.match(StringValidator.formFullStringRegexp(fullStringPattern, caseInsensitiveSearch))) {
        stringContentValid = false;
        stringContent.hasOtherSymbols = true;
      }

      if (!stringContentValid) {
        valid = false;
        validatorErrors.stringContent = stringContent;
      }

      return valid ? null : validatorErrors;
    };
  }

  private static formCharactersPattern(charactersValidator: CharactersValidatorContent): [string, boolean] {
    const useBothCases = charactersValidator.charactersCases.length === 2;

    let charactersRegexp = '';
    charactersValidator.alphabets.forEach(alphabet => {
      if (alphabet === Alphabet.CYRILLIC) {
        if (useBothCases) {
          charactersRegexp += 'а-я';
        } else {
          charactersValidator.charactersCases.forEach(charactersCase => {
            if (charactersCase === CharacterCase.LOWERCASE) {
              charactersRegexp += 'а-я';
            } else if (charactersCase === CharacterCase.UPPERCASE) {
              charactersRegexp += 'А-Я';
            }
          });
        }
      } else if (alphabet === Alphabet.LATIN) {
        if (useBothCases) {
          charactersRegexp += 'a-z';
        } else {
          charactersValidator.charactersCases.forEach(charactersCase => {
            if (charactersCase === CharacterCase.LOWERCASE) {
              charactersRegexp += 'a-z';
            } else if (charactersCase === CharacterCase.UPPERCASE) {
              charactersRegexp += 'A-Z';
            }
          });
        }
      }
    });

    return [charactersRegexp, useBothCases];
  }

  private static formCharactersRegexp(charactersRegexp: string, useBothCases: boolean): RegExp {
    return useBothCases ? new RegExp('[' + charactersRegexp + ']', 'i') : new RegExp('[' + charactersRegexp + ']');
  }

  private static formFullStringRegexp(stringRegexp: string, useBothCases: boolean): RegExp {
    return useBothCases ? new RegExp('^[' + stringRegexp + ']+$', 'i') : new RegExp('^[' + stringRegexp + ']+$');
  }
}
